import { useEffect, useState } from 'react';

import { GetRetailRateRequestsResponse } from '@classpass/protobuf-definitions/lib/web/grpc/partners/retail_rate_requests';

import URLS from 'constants/urls';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';

import { isRetailRateRequestActionRequired } from '../Components/ServicePricingSection/pricingStatusStepper.helpers';

const createdAtMs = request => request.created_at?.getTime() ?? 0;

const serviceKey = request => request.msi_service_uuid ?? request.class_id ?? request.id;

// The venue endpoint returns the full history (multiple requests per service),
// so a resubmitted-after-rejection service still carries its old REJECTED row.
// Action-required is the *current* status of each service: its latest request.
const latestRequestPerService = requests => {
  const byService = new Map();
  requests.forEach(request => {
    const current = byService.get(serviceKey(request));
    if (!current || createdAtMs(request) > createdAtMs(current)) {
      byService.set(serviceKey(request), request);
    }
  });
  return Array.from(byService.values());
};

const fetchVenueRequestsHaveRejection = async venueId => {
  const response = await httpClient.get(URLS.manageServices.services.retailRateRequestsByVenue(venueId));
  const { retailRateRequest = [] } = GetRetailRateRequestsResponse.fromJSON(response.data);
  return latestRequestPerService(retailRateRequest).some(isRetailRateRequestActionRequired);
};

export default function useVenuePricingActionRequired(venueId, enabled = true) {
  const [hasRejectedRequest, setHasRejectedRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Gate the request on the flag so non-flagged venues never hit the endpoint.
    if (!venueId || !enabled) {
      setHasRejectedRequest(false);
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);

    const load = async () => {
      try {
        const result = await fetchVenueRequestsHaveRejection(venueId);
        if (isMounted) setHasRejectedRequest(result);
      } catch (error) {
        reportError('Failed to fetch venue retail rate requests', { error, venueId });
        if (isMounted) setHasRejectedRequest(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [venueId, enabled]);

  return { hasRejectedRequest, isLoading };
}
